import { useRef } from 'react';
import PropTypes from 'prop-types';
import { useBoolean, useEventListener } from 'usehooks-ts';
import {
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { CHART_TABLE_CELL_VARIANTS } from 'constants/dashboard';
import generateKey from 'helper/generateKey';
import { generateTrendGraph } from 'helper/formatHeatmapGraphData';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { InfoTooltip, NoData } from 'components/simple';

const styles = {
  wrapper: {
    position: 'relative',
    width: '100%',
    height: '100%',
    marginTop: 2,
    marginBottom: 1,
    overflow: 'hidden',
  },
  withGradient: {
    '&::after': {
      position: 'absolute',
      content: '""',
      width: '100%',
      height: 40,
      top: 'calc(100% - 39px)',
      backgroundImage: 'linear-gradient(to bottom, rgba(255, 255, 255, 0), #ffffff)',
    },
  },
  scrollContainer: ({ palette }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    maxHeight: 500,
    minHeight: 150,
    overflowX: 'hidden',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar-track': {
      borderRadius: '4px',
      backgroundColor: palette.colors.lightGrey,
    },
    '&::-webkit-scrollbar': {
      width: 2,
      backgroundColor: palette.colors.lightGrey,
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: '4px',
      backgroundColor: palette.colors.darkGrey,
    },
  }),
  headerCell: ({ palette }) => ({
    color: palette.text.primary,
    paddingX: 0,
    paddingTop: 0,
    paddingBottom: '5px',
    border: 'none',
    lineHeight: 1,
  }),
  headerText: ({ breakpoints }) => ({
    [breakpoints.up('md')]: {
      fontSize: 12,
    },
  }),
  bodyCell: ({ palette, spacing, breakpoints }) => ({
    color: palette.text.primary,
    padding: spacing(1, 0.5),
    borderTop: `1px solid ${palette.colors.lightGrey}`,
    borderBottom: 'none',
    [breakpoints.up('sm')]: {
      paddingInline: 1,
    },
    [breakpoints.up('md')]: {
      paddingBlock: 2,
    },
  }),
  circle: ({ breakpoints }) => ({
    width: 24,
    height: 24,
    borderRadius: '50px',
    [breakpoints.up('md')]: {
      width: 32,
      height: 32,
    },
  }),
  trendChart: {
    width: 40,
    mx: 'auto',
  },
  table: { tableLayout: 'fixed' },
};

const renderHeaderContent = (header) => {
  if (header === 'Score') {
    return (
      <>
        {header}
        <br />
        (out of 7)
      </>
    );
  }

  if (header === 'All Time') {
    return (
      <>
        All
        <br />
        Time
      </>
    );
  }

  return header;
};

const renderCellContent = (cell) => {
  switch (cell.variant) {
    case CHART_TABLE_CELL_VARIANTS.CIRCLE:
      return <Box sx={[styles.circle, { background: cell.color }]} />;
    case CHART_TABLE_CELL_VARIANTS.LABEL:
      return (
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" component="span">
            {cell.text}
          </Typography>
          {cell.tooltip && <InfoTooltip titleText={cell.tooltip} />}
        </Stack>
      );
    case CHART_TABLE_CELL_VARIANTS.TREND:
      return <Box sx={styles.trendChart}>{generateTrendGraph(cell.value)}</Box>;
    case CHART_TABLE_CELL_VARIANTS.SCORE:
      return <Typography variant="caption">{cell.value}</Typography>;
    default:
      return null;
  }
};

const ChartTable = ({ containerSx = null, data = {}, noData = false }) => {
  const { getResponsiveValueForCurrent } = useBreakpointFlags();

  const tableContainerRef = useRef(null);
  const { value: isScrollable, setValue: setIsScrollable } = useBoolean();

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current || {};
    const scrollableContentHeight = scrollHeight - clientHeight;

    setIsScrollable(Math.abs(scrollTop - scrollableContentHeight) > 5);
  };

  useEventListener('scroll', handleScroll, tableContainerRef);

  if (noData) {
    return <NoData />;
  }

  const headers = data?.header;
  const rows = data?.columns;
  const columnVariants = rows?.[0]?.cells?.map((cell) => cell.variant) ?? [];
  const columnWidths = {
    [CHART_TABLE_CELL_VARIANTS.CIRCLE]: getResponsiveValueForCurrent({ xs: 32, sm: 40, md: 48 }),
    [CHART_TABLE_CELL_VARIANTS.TREND]: getResponsiveValueForCurrent({ xs: 48, md: 56 }),
    [CHART_TABLE_CELL_VARIANTS.SCORE]: getResponsiveValueForCurrent({ xs: 48, md: 56 }),
  };

  return (
    <Box sx={[styles.wrapper, isScrollable && styles.withGradient]}>
      <TableContainer ref={tableContainerRef} sx={[styles.scrollContainer, containerSx]}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow>
              {headers?.map((header, headerIndex) => (
                <TableCell
                  key={generateKey()}
                  align="center"
                  width={columnWidths[columnVariants[headerIndex]]}
                  sx={styles.headerCell}
                >
                  <Typography variant="caption" component="span" sx={styles.headerText}>
                    {renderHeaderContent(header)}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows?.map((row) => (
              <TableRow key={row.id}>
                {row.cells.map((cell) => {
                  const isLabelCell = cell.variant === CHART_TABLE_CELL_VARIANTS.LABEL;

                  return (
                    <TableCell
                      key={generateKey()}
                      align={isLabelCell ? 'left' : 'center'}
                      width={columnWidths[cell.variant]}
                      sx={styles.bodyCell}
                    >
                      {renderCellContent(cell)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

const cellShape = PropTypes.shape({
  variant: PropTypes.oneOf(Object.values(CHART_TABLE_CELL_VARIANTS)).isRequired,
  color: PropTypes.string,
  text: PropTypes.string,
  tooltip: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
});

ChartTable.propTypes = {
  containerSx: PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.array]),
  data: PropTypes.shape({
    header: PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node, PropTypes.element]),
    ),
    columns: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        cells: PropTypes.arrayOf(cellShape),
      }),
    ),
  }),
  noData: PropTypes.bool,
};
export default ChartTable;
